import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CONTENT = {
  en: {
    titleLatin: 'Aevum Dirigere',
    titleZh: 'Life Guide',
    objectiveTree: {
      title: 'Objective Tree',
      nodes: [
        {
          id: 'obj-universe',
          label: 'Universe Laws',
          detail:
            'The foundation of all phenomena — unchanging order, constraints, and repeatable patterns that exist independently of any individual will.',
        },
        {
          id: 'obj-species',
          label: 'Species',
          detail:
            'Intelligent life forms that have emerged under the laws of the universe.',
          branches: [
            {
              id: 'obj-carbon',
              label: 'Carbon-based Life · Humans',
              detail:
                'Carbon-based organisms — in particular humans — are the dominant intelligent species known so far, shaped by billions of years of evolution.',
              nodes: [
                {
                  id: 'obj-society',
                  label: 'Human Society',
                  detail:
                    'Cooperation, institutions, economics, and culture emerge when humans interact at scale within the constraints set by universe laws.',
                },
              ],
            },
            {
              id: 'obj-silicon',
              label: 'Silicon-based Life',
              detail:
                'Currently a theoretical possibility — a potential intelligent form beyond biology, not yet observed but logically conceivable under universe laws.',
            },
          ],
        },
      ],
    },
    subjectiveTree: {
      title: 'Subjective Tree',
      nodes: [
        {
          id: 'sub-thought',
          label: 'Thought',
          detail:
            'Thought is the first-order weapon. Without systematic thinking, one drifts further from reality. Structured thought is humanity\'s closest approximation to the real world.',
        },
        {
          id: 'sub-thought-universe',
          label: 'Thought & Universe',
          detail:
            'Perhaps the universe contains no thought. Perhaps it does. Perhaps it simply does not care. Either way, so far, thought is humanity\'s best tool for approaching truth.',
        },
        {
          id: 'sub-wealth',
          label: 'Wealth',
          detail:
            'The universe has no concept of wealth. Wealth is recognition by an intelligent species (humans) — or the degree to which needs are met. It can be tangible or intangible (skill, technology), and it can grow.',
        },
      ],
    },
  },
  zh: {
    titleLatin: 'Aevum Dirigere',
    titleZh: '人生指南',
    objectiveTree: {
      title: '客观树',
      nodes: [
        {
          id: 'obj-universe',
          label: '宇宙规律',
          detail: '一切现象的底层约束与可重复模式，不以任何个体意志为转移。',
        },
        {
          id: 'obj-species',
          label: '物种',
          detail: '在宇宙规律下涌现出的智慧生命形态。',
          branches: [
            {
              id: 'obj-carbon',
              label: '碳基生命人类',
              detail:
                '碳基生物——尤其是人类——是目前已知的主导智慧物种，经过数十亿年进化形成。',
              nodes: [
                {
                  id: 'obj-society',
                  label: '人类社会',
                  detail:
                    '当人类在宇宙规律的约束下规模化协作，制度、经济、文化与组织结构便随之涌现。',
                },
              ],
            },
            {
              id: 'obj-silicon',
              label: '硅基生命',
              detail:
                '当前仍是理论层面的可能性——生物之外的智慧形式，尚未被观测，但在宇宙规律框架下逻辑上是可能的。',
            },
          ],
        },
      ],
    },
    subjectiveTree: {
      title: '主观树',
      nodes: [
        {
          id: 'sub-thought',
          label: '思想',
          detail:
            '思想是第一序列武器。如果没有成体系的思想，那么离真实的世界会远一点。成体系的思想是人类无限逼近真实世界的路径。',
        },
        {
          id: 'sub-thought-universe',
          label: '思想与宇宙',
          detail:
            '也许宇宙不存在思想；也许宇宙存在思想；也许宇宙根本不 care 思想。但到目前为止，思想是人类了解真实世界的无限逼近。',
        },
        {
          id: 'sub-wealth',
          label: '关于财富',
          detail:
            '宇宙没有财富的概念。财富是智慧物种（人类）的认可（定义），或需求得到满足（也有程度之分）。财富是满足需求的实物或非实物（技术或能力），可增长。',
        },
      ],
    },
  },
};

/* ── Layout constants (方案一: Custom SVG) ───────────────────────────────── */
const NODE_H = 48;
const NODE_GAP = 32;
const V_STEM = 20;
const PAD_Y = 12;
const STROKE_W = 2;
const DOT_R = 4;

function rowTop(index) {
  return PAD_Y + index * (NODE_H + NODE_GAP);
}

/* ── Connector: stroke-dash 描边动画 (方案一) ─────────────────────────────── */
function ConnectorPath({ d }) {
  return (
    <motion.path
      d={d}
      fill="none"
      stroke="var(--aevdir-stroke, #cbd5e1)"
      strokeWidth={STROKE_W}
      strokeLinecap="square"
      strokeLinejoin="miter"
      initial={{ pathLength: 0, opacity: 0.65 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{
        pathLength: { duration: 0.48, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0.22 },
      }}
    />
  );
}

/* ── Dot + foreignObject node ─────────────────────────────────────────────── */
function SvgTreeNodeRow({
  node,
  foX,
  foY,
  foW,
  foH,
  active,
  linkTop,
  linkBottom,
  onHoverStart,
  onHoverEnd,
  onTogglePin,
}) {
  const centerX = foX + foW / 2;

  return (
    <g className="aevdir-svg-node-row">
      <foreignObject x={foX} y={foY} width={foW} height={foH} className="aevdir-svg-fo">
        <div xmlns="http://www.w3.org/1999/xhtml" className="aevdir-svg-fo-inner">
          <motion.button
            type="button"
            className={`aevdir-node-btn${active ? ' is-active' : ''}`}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
            onClick={onTogglePin}
            aria-expanded={active}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 520, damping: 28 }}
          >
            <span className="aevdir-node-label">{node.label}</span>
          </motion.button>
        </div>
      </foreignObject>
      {linkTop ? (
        <circle
          cx={centerX}
          cy={foY}
          r={DOT_R}
          className={`aevdir-svg-dot${active ? ' is-active' : ''}`}
          fill="var(--aevdir-dot-fill, #fff)"
          stroke="var(--aevdir-dot-stroke, #3b82f6)"
          strokeWidth={1.5}
          pointerEvents="none"
        />
      ) : null}
      {linkBottom ? (
        <circle
          cx={centerX}
          cy={foY + foH}
          r={DOT_R}
          className={`aevdir-svg-dot${active ? ' is-active' : ''}`}
          fill="var(--aevdir-dot-fill, #fff)"
          stroke="var(--aevdir-dot-stroke, #3b82f6)"
          strokeWidth={1.5}
          pointerEvents="none"
        />
      ) : null}
    </g>
  );
}

/* ── Linear tree (主观树) ─────────────────────────────────────────────────── */
function layoutLinearTree(nodes, W) {
  const nodeCount = nodes.length;
  const x = 12;
  const w = Math.max(120, W - 24);
  const centerX = x + w / 2;
  const paths = [];
  for (let i = 1; i < nodeCount; i += 1) {
    const yPrev = rowTop(i - 1) + NODE_H;
    const yCurr = rowTop(i);
    paths.push({
      id: `v-${i}`,
      d: `M ${centerX} ${yPrev} L ${centerX} ${yCurr}`,
    });
  }
  const height = PAD_Y * 2 + nodeCount * NODE_H + Math.max(0, nodeCount - 1) * NODE_GAP;
  const placements = [];
  for (let i = 0; i < nodeCount; i += 1) {
    placements.push({
      key: nodes[i].id,
      node: nodes[i],
      fo: { x, y: rowTop(i), w, h: NODE_H },
      linkTop: i > 0,
      linkBottom: i < nodeCount - 1,
    });
  }
  return { paths, height, placements };
}

/* ── Objective tree with single fork (客观树当前数据) ─────────────────────── */
function layoutObjectiveTree(nodes, W) {
  const margin = 12;
  const colGap = 20;
  const colW = Math.max(90, (W - margin * 2 - colGap) / 2);
  const mainX = margin;
  const mainW = Math.max(120, W - margin * 2);
  const leftX = margin;
  const rightX = leftX + colW + colGap;
  const mainCx = mainX + mainW / 2;
  const leftCx = leftX + colW / 2;
  const rightCx = rightX + colW / 2;

  const yUniverse = rowTop(0);
  const ySpecies = rowTop(1);
  const ySpeciesBottom = ySpecies + NODE_H;
  const yBar = ySpeciesBottom + V_STEM;
  const yBranchTop = yBar + V_STEM;
  const yCarbon = yBranchTop;
  const ySilicon = yBranchTop;

  const forkNode = nodes.find((n) => n.branches);
  const branches = forkNode?.branches || [];
  const carbon = branches[0];
  const silicon = branches[1];
  const society = carbon?.nodes?.[0];
  const hasSociety = Boolean(society);

  const ySociety = hasSociety ? yCarbon + NODE_H + NODE_GAP : yCarbon;

  const lastY = hasSociety ? ySociety + NODE_H : Math.max(yCarbon + NODE_H, ySilicon + NODE_H);
  const height = lastY + PAD_Y;

  const paths = [];

  paths.push({
    id: 'main-0-1',
    d: `M ${mainCx} ${yUniverse + NODE_H} L ${mainCx} ${ySpecies}`,
  });

  paths.push({
    id: 'fork-tee',
    d: `M ${mainCx} ${ySpeciesBottom} L ${mainCx} ${yBar} L ${leftCx} ${yBar} L ${rightCx} ${yBar}`,
  });

  paths.push({
    id: 'stem-left',
    d: `M ${leftCx} ${yBar} L ${leftCx} ${yCarbon}`,
  });
  paths.push({
    id: 'stem-right',
    d: `M ${rightCx} ${yBar} L ${rightCx} ${ySilicon}`,
  });

  if (hasSociety) {
    paths.push({
      id: 'carbon-society',
      d: `M ${leftCx} ${yCarbon + NODE_H} L ${leftCx} ${ySociety}`,
    });
  }

  const placements = [];

  placements.push({
    key: nodes[0].id,
    node: nodes[0],
    fo: { x: mainX, y: yUniverse, w: mainW, h: NODE_H },
    linkTop: false,
    linkBottom: true,
  });

  placements.push({
    key: nodes[1].id,
    node: nodes[1],
    fo: { x: mainX, y: ySpecies, w: mainW, h: NODE_H },
    linkTop: true,
    linkBottom: true,
  });

  placements.push({
    key: carbon.id,
    node: carbon,
    fo: { x: leftX, y: yCarbon, w: colW, h: NODE_H },
    linkTop: true,
    linkBottom: Boolean(society),
  });

  if (society) {
    placements.push({
      key: society.id,
      node: society,
      fo: { x: leftX, y: ySociety, w: colW, h: NODE_H },
      linkTop: true,
      linkBottom: false,
    });
  }

  placements.push({
    key: silicon.id,
    node: silicon,
    fo: { x: rightX, y: ySilicon, w: colW, h: NODE_H },
    linkTop: true,
    linkBottom: false,
  });

  return { paths, height, placements };
}

/* ── Tree column ───────────────────────────────────────────────────────────── */
function SvgTreeColumn({ title, nodes, tooltipSide }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(300);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [pinnedKey, setPinnedKey] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr && cr.width > 0) setWidth(Math.floor(cr.width));
    });
    ro.observe(el);
    setWidth(Math.floor(el.getBoundingClientRect().width) || 300);
    return () => ro.disconnect();
  }, []);

  const hasFork = nodes.some((n) => n.branches);
  const layout = hasFork
    ? layoutObjectiveTree(nodes, width)
    : layoutLinearTree(nodes, width);

  const forkSlice = hasFork ? 2 : 0;
  const activeKey = pinnedKey || hoveredKey;
  const activePlacement = layout.placements.find((pl) => pl.key === activeKey);
  const activeNode = activePlacement?.node;
  const tooltipStyle = activePlacement
    ? tooltipSide === 'right'
      ? { top: activePlacement.fo.y + NODE_H - 16, left: activePlacement.fo.x }
      : {
          top: activePlacement.fo.y + NODE_H - 16,
          right: width - (activePlacement.fo.x + activePlacement.fo.w),
        }
    : null;

  return (
    <section className="aevdir-tree-col">
      <h3 className="aevdir-tree-heading">{title}</h3>
      <div ref={wrapRef} className="aevdir-svg-tree-wrap">
        <svg
          className="aevdir-svg-tree"
          width="100%"
          height={layout.height}
          viewBox={`0 0 ${width} ${layout.height}`}
          preserveAspectRatio="xMinYMin meet"
        >
          <title>{title}</title>
          {layout.paths.map((p) => (
            <ConnectorPath key={p.id} d={p.d} />
          ))}

          {layout.placements.slice(0, forkSlice).map((pl) => (
            <SvgTreeNodeRow
              key={pl.key}
              node={pl.node}
              foX={pl.fo.x}
              foY={pl.fo.y}
              foW={pl.fo.w}
              foH={pl.fo.h}
              active={activeKey === pl.key}
              linkTop={pl.linkTop}
              linkBottom={pl.linkBottom}
              onHoverStart={() => setHoveredKey(pl.key)}
              onHoverEnd={() => setHoveredKey((prev) => (prev === pl.key ? null : prev))}
              onTogglePin={() => setPinnedKey((prev) => (prev === pl.key ? null : pl.key))}
            />
          ))}

          {hasFork ? (
            <motion.g
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 30, delay: 0.06 }}
            >
              {layout.placements.slice(forkSlice).map((pl) => (
                <SvgTreeNodeRow
                  key={pl.key}
                  node={pl.node}
                  foX={pl.fo.x}
                  foY={pl.fo.y}
                  foW={pl.fo.w}
                  foH={pl.fo.h}
                  active={activeKey === pl.key}
                  linkTop={pl.linkTop}
              linkBottom={pl.linkBottom}
                  onHoverStart={() => setHoveredKey(pl.key)}
                  onHoverEnd={() => setHoveredKey((prev) => (prev === pl.key ? null : prev))}
                  onTogglePin={() => setPinnedKey((prev) => (prev === pl.key ? null : pl.key))}
                />
              ))}
            </motion.g>
          ) : (
            layout.placements.map((pl) => (
              <SvgTreeNodeRow
                key={pl.key}
                node={pl.node}
                foX={pl.fo.x}
                foY={pl.fo.y}
                foW={pl.fo.w}
                foH={pl.fo.h}
                active={activeKey === pl.key}
                linkTop={pl.linkTop}
              linkBottom={pl.linkBottom}
                onHoverStart={() => setHoveredKey(pl.key)}
                onHoverEnd={() => setHoveredKey((prev) => (prev === pl.key ? null : prev))}
                onTogglePin={() => setPinnedKey((prev) => (prev === pl.key ? null : pl.key))}
              />
            ))
          )}
        </svg>
        <div className="aevdir-tooltip-layer" aria-live="polite">
          <AnimatePresence>
            {activeNode && tooltipStyle ? (
              <motion.div
                key={activeKey}
                className={`aevdir-tooltip ${tooltipSide}`}
                role="tooltip"
                style={tooltipStyle}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {activeNode.detail}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */
function AevdirPage({ lang }) {
  const t = CONTENT[lang] || CONTENT.en;

  return (
    <div className="aevdir-page">
      <header className="aevdir-hero">
        <h1 className="aevdir-hero-title">
          <span className="aevdir-hero-mid">{t.titleLatin}</span>
          <span className="aevdir-hero-dot"> · </span>
          <span className="aevdir-hero-sub">{t.titleZh}</span>
        </h1>
      </header>

      <div className="aevdir-trees">
        <SvgTreeColumn
          title={t.objectiveTree.title}
          nodes={t.objectiveTree.nodes}
          tooltipSide="right"
        />
        <div className="aevdir-trees-divider" aria-hidden="true" />
        <SvgTreeColumn
          title={t.subjectiveTree.title}
          nodes={t.subjectiveTree.nodes}
          tooltipSide="left"
        />
      </div>
    </div>
  );
}

export default AevdirPage;
